import { Schema } from 'effect';
import { Location2DSchema, Location3DSchema } from './location';
import { PackageSchema } from './package';
import { PickupPointSchema } from './pickupPoint';
import { DockPointSchema } from './dockPoint';
import { TimestampSchema } from './timestamp';
import { VehicleIdSchema } from './vehicle';

export const MissionIdSchema = Schema.String;
export type MissionId = Schema.Schema.Type<typeof MissionIdSchema>;

// ============================================================================
// Live Mission Notifications
// ============================================================================

export const MissionCreatedNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    vehicle_id: VehicleIdSchema,
    package: Schema.optional(Schema.NullOr(Schema.encodedSchema(PackageSchema))),
    pickup_point: Schema.optional(Schema.NullOr(Schema.encodedSchema(PickupPointSchema))),
    dock_point: Schema.optional(Schema.NullOr(Schema.encodedSchema(DockPointSchema)))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    vehicleId: VehicleIdSchema,
    package: Schema.optional(Schema.NullOr(PackageSchema)),
    pickupPoint: Schema.optional(Schema.NullOr(PickupPointSchema)),
    dockPoint: Schema.optional(Schema.NullOr(DockPointSchema))
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      vehicleId: api.vehicle_id,
      package: api.package,
      pickupPoint: api.pickup_point,
      dockPoint: api.dock_point
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      vehicle_id: client.vehicleId,
      package: client.package,
      pickup_point: client.pickupPoint,
      dock_point: client.dockPoint
    })
  }
);
export type MissionCreatedNotification = Schema.Schema.Type<typeof MissionCreatedNotificationSchema>;

export const MissionUpdatedNotificationSchema = MissionCreatedNotificationSchema;
export type MissionUpdatedNotification = MissionCreatedNotification;

export const MissionFinishedNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema
  }),
  Schema.Struct({
    missionId: MissionIdSchema
  }),
  {
    strict: true,
    decode: (api) => ({ missionId: api.mission_id }),
    encode: (client) => ({ mission_id: client.missionId })
  }
);
export type MissionFinishedNotification = Schema.Schema.Type<typeof MissionFinishedNotificationSchema>;

// ============================================================================
// Mission State Notifications
// ============================================================================

export const MissionStateNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema
  }),
  Schema.Struct({
    missionId: MissionIdSchema
  }),
  {
    strict: true,
    decode: (api) => ({ missionId: api.mission_id }),
    encode: (client) => ({ mission_id: client.missionId })
  }
);
export type MissionStateNotification = Schema.Schema.Type<typeof MissionStateNotificationSchema>;

export const MissionStatus = {
  CREATED: 'created',
  PREFLIGHT: 'preflight',
  TAKING_OFF: 'taking_off',
  INFLIGHT_TO_PICKUP: 'inflight_to_pickup',
  PICKING_UP: 'picking_up',
  WAITING_FOR_PACKAGE_LOADED_CONFIRMATION: 'waiting_for_package_loaded_confirmation',
  INFLIGHT_TO_DOCK: 'inflight_to_dock',
  LANDING_ON_DOCK: 'landing_on_dock',
  WAITING_ON_DOCK: 'waiting_on_dock',
  PICKING_UP_FROM_DOCK: 'picking_up_from_dock',
  TAKING_OFF_FROM_DOCK: 'taking_off_from_dock',
  INFLIGHT_TO_DESTINATION: 'inflight_to_destination',
  INFLIGHT_HOME: 'inflight_home',
  LANDING_HOME: 'landing_home',
  WAITING_FOR_WIRE_DROP_CONFIRMATION: 'waiting_for_wire_drop_confirmation',
  PERFORMING_WIRE_DROP: 'performing_wire_drop',
  HOVERING_PAUSED: 'hovering_paused',
  SAFING_UP: 'safing_up',
  SAFED_UP: 'safed_up',
  DESCENDING: 'descending',
  DESCENDED: 'descended',
  ABORT_HOME: 'abort_home',
  FC_RTL: 'fc_rtl',
  EMERGENCY_LANDING: 'emergency_landing',
  RC_IN_CONTROL: 'rc_in_control',
  MISSION_COMPUTER_POSTFLIGHT: 'mc_postflight',
  EXECUTING_FALLBACK_MISSION: 'executing_fallback_mission',
  FC_LOSS: 'fc_loss',
  POSTFLIGHT: 'postflight',
  COMPLETED: 'completed',
  ABORTED: 'aborted',
  PARACHUTE_TRIGGERED: 'parachute_triggered',
  VEHICLE_DISCONNECTED: 'vehicle_disconnected'
} as const;

export const MissionStatusSchema = Schema.Literal(
  MissionStatus.CREATED,
  MissionStatus.PREFLIGHT,
  MissionStatus.TAKING_OFF,
  MissionStatus.INFLIGHT_TO_PICKUP,
  MissionStatus.PICKING_UP,
  MissionStatus.WAITING_FOR_PACKAGE_LOADED_CONFIRMATION,
  MissionStatus.INFLIGHT_TO_DOCK,
  MissionStatus.LANDING_ON_DOCK,
  MissionStatus.WAITING_ON_DOCK,
  MissionStatus.PICKING_UP_FROM_DOCK,
  MissionStatus.TAKING_OFF_FROM_DOCK,
  MissionStatus.INFLIGHT_TO_DESTINATION,
  MissionStatus.INFLIGHT_HOME,
  MissionStatus.LANDING_HOME,
  MissionStatus.WAITING_FOR_WIRE_DROP_CONFIRMATION,
  MissionStatus.PERFORMING_WIRE_DROP,
  MissionStatus.HOVERING_PAUSED,
  MissionStatus.SAFING_UP,
  MissionStatus.SAFED_UP,
  MissionStatus.DESCENDING,
  MissionStatus.DESCENDED,
  MissionStatus.ABORT_HOME,
  MissionStatus.FC_RTL,
  MissionStatus.EMERGENCY_LANDING,
  MissionStatus.RC_IN_CONTROL,
  MissionStatus.MISSION_COMPUTER_POSTFLIGHT,
  MissionStatus.EXECUTING_FALLBACK_MISSION,
  MissionStatus.FC_LOSS,
  MissionStatus.POSTFLIGHT,
  MissionStatus.COMPLETED,
  MissionStatus.ABORTED,
  MissionStatus.PARACHUTE_TRIGGERED,
  MissionStatus.VEHICLE_DISCONNECTED
);
export type MissionStatus = Schema.Schema.Type<typeof MissionStatusSchema>;

export const MissionFailureMode = {
  UNKNOWN_VEHICLE_DISCONNECTED: 'unknown',
  NOMINAL: 'nominal',
  RTL: 'rtl',
  SAFING_UP: 'safing_up',
  SAFED_UP: 'safed_up',
  DESCENDING: 'descending',
  DESCENDED: 'descended',
  EMERGENCY_LAND: 'emergency_land',
  FC_RTL: 'fc_rtl',
  FC_LOSS: 'fc_loss',
  FTS_TRIGGERED: 'fts_triggered'
} as const;

export const MissionFailureModeSchema = Schema.Literal(
  MissionFailureMode.UNKNOWN_VEHICLE_DISCONNECTED,
  MissionFailureMode.NOMINAL,
  MissionFailureMode.RTL,
  MissionFailureMode.SAFING_UP,
  MissionFailureMode.SAFED_UP,
  MissionFailureMode.DESCENDING,
  MissionFailureMode.DESCENDED,
  MissionFailureMode.EMERGENCY_LAND,
  MissionFailureMode.FC_RTL,
  MissionFailureMode.FC_LOSS,
  MissionFailureMode.FTS_TRIGGERED
);
export type MissionFailureMode = Schema.Schema.Type<typeof MissionFailureModeSchema>;

export const MissionDetailedStateNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    status: MissionStatusSchema,
    until_ms: Schema.optional(Schema.NullOr(Schema.Number)),
    failure_mode: MissionFailureModeSchema,
    has_package: Schema.optional(Schema.Boolean)
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    status: MissionStatusSchema,
    untilMs: Schema.optional(Schema.NullOr(Schema.Number)),
    failureMode: MissionFailureModeSchema,
    hasPackage: Schema.optional(Schema.Boolean)
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      status: api.status,
      untilMs: api.until_ms,
      failureMode: api.failure_mode,
      hasPackage: api.has_package
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      status: client.status,
      until_ms: client.untilMs,
      failure_mode: client.failureMode,
      has_package: client.hasPackage
    })
  }
);
export type MissionDetailedStateNotification = Schema.Schema.Type<typeof MissionDetailedStateNotificationSchema>;

export const AttitudeSchema = Schema.Struct({
  roll: Schema.Number,
  pitch: Schema.Number,
  yaw: Schema.Number
});
export type Attitude = Schema.Schema.Type<typeof AttitudeSchema>;

export const MissionFlightDataNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    location: Location3DSchema,
    speed_ms: Schema.Number,
    attitude: AttitudeSchema,
    gps_signal_level: Schema.Number,
    battery_pct: Schema.Number
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    location: Location3DSchema,
    speedMs: Schema.Number,
    attitude: AttitudeSchema,
    gpsSignalLevel: Schema.Number,
    batteryPct: Schema.Number
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      location: api.location,
      speedMs: api.speed_ms,
      attitude: api.attitude,
      gpsSignalLevel: api.gps_signal_level,
      batteryPct: api.battery_pct
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      location: client.location,
      speed_ms: client.speedMs,
      attitude: client.attitude,
      gps_signal_level: client.gpsSignalLevel,
      battery_pct: client.batteryPct
    })
  }
);
export type MissionFlightDataNotification = Schema.Schema.Type<typeof MissionFlightDataNotificationSchema>;

export const MissionEtasNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    progress: Schema.optional(Schema.NullOr(Schema.Number))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    progress: Schema.optional(Schema.NullOr(Schema.Number))
  }),
  {
    strict: true,
    decode: (api) => ({ missionId: api.mission_id, progress: api.progress }),
    encode: (client) => ({
      mission_id: client.missionId,
      progress: client.progress
    })
  }
);
export type MissionEtasNotification = Schema.Schema.Type<typeof MissionEtasNotificationSchema>;

export const MissionTimestampsNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    mc_preflight_timestamp: Schema.optional(Schema.NullOr(TimestampSchema)),
    takeoff_timestamp: Schema.optional(Schema.NullOr(TimestampSchema)),
    initial_failure_mode_timestamp: Schema.optional(Schema.NullOr(TimestampSchema)),
    dock_landing_timestamp: Schema.optional(Schema.NullOr(TimestampSchema))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    mcPreflightTimestamp: Schema.optional(Schema.NullOr(Schema.String)),
    takeoffTimestamp: Schema.optional(Schema.NullOr(Schema.String)),
    initialFailureModeTimestamp: Schema.optional(Schema.NullOr(Schema.String)),
    dockLandingTimestamp: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      mcPreflightTimestamp: api.mc_preflight_timestamp,
      takeoffTimestamp: api.takeoff_timestamp,
      initialFailureModeTimestamp: api.initial_failure_mode_timestamp,
      dockLandingTimestamp: api.dock_landing_timestamp
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      mc_preflight_timestamp: client.mcPreflightTimestamp,
      takeoff_timestamp: client.takeoffTimestamp,
      initial_failure_mode_timestamp: client.initialFailureModeTimestamp,
      dock_landing_timestamp: client.dockLandingTimestamp
    })
  }
);
export type MissionTimestampsNotification = Schema.Schema.Type<typeof MissionTimestampsNotificationSchema>;

export const MissionRpicInControlNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    is_rpic_in_control: Schema.Boolean,
    is_mission_rpic: Schema.Boolean
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    isRpicInControl: Schema.Boolean,
    isMissionRpic: Schema.Boolean
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      isRpicInControl: api.is_rpic_in_control,
      isMissionRpic: api.is_mission_rpic
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      is_rpic_in_control: client.isRpicInControl,
      is_mission_rpic: client.isMissionRpic
    })
  }
);
export type MissionRpicInControlNotification = Schema.Schema.Type<typeof MissionRpicInControlNotificationSchema>;

export const LogMessageLevel = {
  INFO: 'info',
  WARNING: 'warn',
  ERROR: 'error'
} as const;

export const LogMessageLevelSchema = Schema.Literal(
  LogMessageLevel.INFO,
  LogMessageLevel.WARNING,
  LogMessageLevel.ERROR
);
export type LogMessageLevel = Schema.Schema.Type<typeof LogMessageLevelSchema>;

export const MissionLogMessageNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    level: LogMessageLevelSchema,
    text: Schema.String,
    timestamp: Schema.optional(Schema.NullOr(TimestampSchema))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    level: LogMessageLevelSchema,
    text: Schema.String,
    timestamp: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      level: api.level,
      text: api.text,
      timestamp: api.timestamp
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      level: client.level,
      text: client.text,
      timestamp: client.timestamp
    })
  }
);
export type MissionLogMessageNotification = Schema.Schema.Type<typeof MissionLogMessageNotificationSchema>;

export const FlightPathNodeSchema = Schema.Struct({
  location: Location2DSchema
});
export type FlightPathNode = Schema.Schema.Type<typeof FlightPathNodeSchema>;

export const MissionRouteNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    route: Schema.Array(Schema.encodedSchema(FlightPathNodeSchema)),
    route_break_indices: Schema.optional(Schema.NullOr(Schema.Array(Schema.Number)))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    route: Schema.Array(FlightPathNodeSchema),
    routeBreakIndices: Schema.optional(Schema.NullOr(Schema.Array(Schema.Number)))
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      route: api.route,
      routeBreakIndices: api.route_break_indices
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      route: client.route,
      route_break_indices: client.routeBreakIndices
    })
  }
);
export type MissionRouteNotification = Schema.Schema.Type<typeof MissionRouteNotificationSchema>;

export const SafePointSchema = Schema.Struct({
  location: Location2DSchema
});
export type SafePoint = Schema.Schema.Type<typeof SafePointSchema>;

export const MissionFailureModeRouteInfoSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    route_to_safe_point: Schema.Array(Schema.encodedSchema(Location2DSchema)),
    route_to_safe_point_base_index: Schema.optional(Schema.NullOr(Schema.Number)),
    safe_points: Schema.Array(Schema.encodedSchema(SafePointSchema))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    routeToSafePoint: Schema.Array(Location2DSchema),
    routeToSafePointBaseIndex: Schema.optional(Schema.NullOr(Schema.Number)),
    safePoints: Schema.Array(SafePointSchema)
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      routeToSafePoint: api.route_to_safe_point,
      routeToSafePointBaseIndex: api.route_to_safe_point_base_index,
      safePoints: api.safe_points
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      route_to_safe_point: client.routeToSafePoint,
      route_to_safe_point_base_index: client.routeToSafePointBaseIndex,
      safe_points: client.safePoints
    })
  }
);
export type MissionFailureModeRouteInfo = Schema.Schema.Type<typeof MissionFailureModeRouteInfoSchema>;

export const MissionPreflightStateNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    takeoff_prohibition: Schema.optional(Schema.NullOr(Schema.String))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    takeoffProhibition: Schema.optional(Schema.NullOr(Schema.String))
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      takeoffProhibition: api.takeoff_prohibition
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      takeoff_prohibition: client.takeoffProhibition
    })
  }
);
export type MissionPreflightStateNotification = Schema.Schema.Type<typeof MissionPreflightStateNotificationSchema>;

export const SegmentSchema = Schema.transform(
  Schema.Struct({
    start_index: Schema.Number,
    end_index: Schema.Number
  }),
  Schema.Struct({
    startIndex: Schema.Number,
    endIndex: Schema.Number
  }),
  {
    strict: true,
    decode: (api) => ({
      startIndex: api.start_index,
      endIndex: api.end_index
    }),
    encode: (client) => ({
      start_index: client.startIndex,
      end_index: client.endIndex
    })
  }
);
export type Segment = Schema.Schema.Type<typeof SegmentSchema>;

export const MissionLockingInfoSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    locked_segments_indices: Schema.Array(Schema.encodedSchema(SegmentSchema))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    lockedSegmentsIndices: Schema.Array(SegmentSchema)
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      lockedSegmentsIndices: api.locked_segments_indices
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      locked_segments_indices: client.lockedSegmentsIndices
    })
  }
);
export type MissionLockingInfo = Schema.Schema.Type<typeof MissionLockingInfoSchema>;

export type MissionLockingInfoNotification = MissionLockingInfo;

export const SimpleMissionRequestSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema
  }),
  Schema.Struct({
    missionId: MissionIdSchema
  }),
  {
    strict: true,
    decode: (api) => ({ missionId: api.mission_id }),
    encode: (client) => ({ mission_id: client.missionId })
  }
);
export type SimpleMissionRequest = Schema.Schema.Type<typeof SimpleMissionRequestSchema>;

export const MissionNoteResponseSchema = Schema.transform(
  Schema.Struct({
    mission_note: Schema.NullOr(Schema.String)
  }),
  Schema.Struct({
    missionNote: Schema.NullOr(Schema.String)
  }),
  {
    strict: true,
    decode: (api) => ({
      missionNote: api.mission_note
    }),
    encode: (client) => ({
      mission_note: client.missionNote
    })
  }
);
export type MissionNoteResponse = Schema.Schema.Type<typeof MissionNoteResponseSchema>;

export const MissionWaypointType = {
  LAUNCHPAD: 'launchpad',
  PICKUP_POINT: 'pickup_point',
  DOCK_POINT: 'dock_point',
  DELIVERY_POINT: 'delivery_point'
} as const;

export const MissionWaypointTypeSchema = Schema.Literal(
  MissionWaypointType.LAUNCHPAD,
  MissionWaypointType.PICKUP_POINT,
  MissionWaypointType.DOCK_POINT,
  MissionWaypointType.DELIVERY_POINT
);
export type MissionWaypointType = Schema.Schema.Type<typeof MissionWaypointTypeSchema>;

export const MissionLegSchema = Schema.Struct({
  from: MissionWaypointTypeSchema,
  to: MissionWaypointTypeSchema
});
export type MissionLeg = Schema.Schema.Type<typeof MissionLegSchema>;

export const MissionProgressNotificationSchema = Schema.transform(
  Schema.Struct({
    mission_id: MissionIdSchema,
    completed_legs: Schema.Array(Schema.encodedSchema(MissionLegSchema)),
    active_leg: Schema.NullOr(Schema.encodedSchema(MissionLegSchema))
  }),
  Schema.Struct({
    missionId: MissionIdSchema,
    completedLegs: Schema.Array(MissionLegSchema),
    activeLeg: Schema.NullOr(MissionLegSchema)
  }),
  {
    strict: true,
    decode: (api) => ({
      missionId: api.mission_id,
      completedLegs: api.completed_legs,
      activeLeg: api.active_leg
    }),
    encode: (client) => ({
      mission_id: client.missionId,
      completed_legs: client.completedLegs,
      active_leg: client.activeLeg
    })
  }
);
export type MissionProgressNotification = Schema.Schema.Type<typeof MissionProgressNotificationSchema>;

export const SetMissionNoteRequestSchema = Schema.transform(
  Schema.Struct({
    mission_note: Schema.String
  }),
  Schema.Struct({
    missionNote: Schema.String
  }),
  {
    strict: true,
    decode: (api) => ({
      missionNote: api.mission_note
    }),
    encode: (client) => ({
      mission_note: client.missionNote
    })
  }
);
export type SetMissionNoteRequest = Schema.Schema.Type<typeof SetMissionNoteRequestSchema>;
